<script setup>
/**
 * PromotionItemsPicker Component
 * Manages items[] array for a promotion
 * Supports searching from catalog store or Firestore
 */
import { ref, computed, watch } from 'vue'
import { useCatalogStore } from '../../../../app/store/catalog.js'
import { getFirebaseDb } from '../../../../core/firebase/firebase.js'
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore'

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:items'])

// Catalog store for in-memory search
const catalogStore = useCatalogStore()

// Local state
const searchQuery = ref('')
const searchResults = ref([])
const isSearching = ref(false)
const searchError = ref(null)

// Debounce timer
let searchTimeout = null

// Watch search query and search
watch(searchQuery, (val) => {
  clearTimeout(searchTimeout)

  if (!val || val.trim().length < 2) {
    searchResults.value = []
    return
  }

  searchTimeout = setTimeout(() => {
    performSearch(val.trim())
  }, 300)
})

// Perform search - try catalog store first, then Firestore
async function performSearch(searchTerm) {
  isSearching.value = true
  searchError.value = null

  try {
    const q = searchTerm.toLowerCase()

    // Try catalog store first if bootstrapped
    if (catalogStore.isBootstrapped && catalogStore.itemsArray.length > 0) {
      const results = catalogStore.itemsArray
        .filter((item) => {
          const name = (item.name || '').toLowerCase()
          const makat = (item.makat || '').toLowerCase()
          const barcode = (item.barcode || '').toLowerCase()
          return name.includes(q) || makat.includes(q) || barcode.includes(q)
        })
        .slice(0, 20)

      searchResults.value = results.map((item) => ({
        makat: item.makat,
        name: item.name || '',
        boxSize: item.boxSize || '',
        barcode: item.barcode || '',
        image: item.image || '',
      }))
    } else {
      // Fallback to Firestore search
      await searchFirestore(searchTerm)
    }
  } catch (err) {
    console.error('[ItemsPicker] Search failed:', err)
    searchError.value = 'שגיאה בחיפוש'
  } finally {
    isSearching.value = false
  }
}

// Firestore search fallback
async function searchFirestore(searchTerm) {
  const db = getFirebaseDb()
  const catalogRef = collection(db, 'Catalog')

  // Search by makat (exact or starts with)
  const makatQuery = query(
    catalogRef,
    where('makat', '>=', searchTerm),
    where('makat', '<=', searchTerm + '\uf8ff'),
    limit(20)
  )

  const snapshot = await getDocs(makatQuery)

  searchResults.value = snapshot.docs.map((doc) => {
    const data = doc.data()
    return {
      makat: doc.id,
      name: data.name || '',
      boxSize: data.boxSize || '',
      barcode: data.barcode || '',
      image: data.image || '',
    }
  })
}

// Check if item is already selected
function isItemSelected(makat) {
  return props.items.some((item) => item.makat === makat)
}

// Add item to selection
function addItem(item) {
  if (isItemSelected(item.makat)) return

  const newItems = [
    ...props.items,
    {
      makat: item.makat,
      name: item.name,
      boxSize: item.boxSize,
      image: item.image || '',
    },
  ]
  emit('update:items', newItems)
}

// Remove item from selection
function removeItem(makat) {
  const newItems = props.items.filter((item) => item.makat !== makat)
  emit('update:items', newItems)
}

// Clear all items
function clearAll() {
  emit('update:items', [])
}

// Move item up in list
function moveUp(index) {
  if (index <= 0) return
  const newItems = [...props.items]
  ;[newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]]
  emit('update:items', newItems)
}

// Move item down in list
function moveDown(index) {
  if (index >= props.items.length - 1) return
  const newItems = [...props.items]
  ;[newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]]
  emit('update:items', newItems)
}

// Clear search
function clearSearch() {
  searchQuery.value = ''
  searchResults.value = []
}
</script>

<template>
  <div class="items-picker">
    <!-- Search Section -->
    <div class="search-section">
      <div class="search-header">
        <span class="search-title">חיפוש פריטים</span>
        <span v-if="catalogStore.isBootstrapped" class="search-hint"> (חיפוש מהקטלוג המקומי) </span>
      </div>
      <div class="search-input-wrapper">
        <svg
          class="search-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="חפש לפי מק״ט, שם או ברקוד..."
        />
        <button v-if="searchQuery" class="clear-btn" @click="clearSearch">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Search Results -->
      <div v-if="searchQuery.length >= 2" class="search-results">
        <div v-if="isSearching" class="search-loading">
          <div class="loading-spinner"></div>
          <span>מחפש...</span>
        </div>
        <div v-else-if="searchError" class="search-error">{{ searchError }}</div>
        <div v-else-if="searchResults.length === 0" class="search-empty">לא נמצאו תוצאות</div>
        <div v-else class="results-list">
          <button
            v-for="item in searchResults"
            :key="item.makat"
            class="result-item"
            :class="{ selected: isItemSelected(item.makat) }"
            :disabled="isItemSelected(item.makat)"
            @click="addItem(item)"
          >
            <span class="result-makat">{{ item.makat }}</span>
            <span class="result-name">{{ item.name }}</span>
            <span v-if="item.boxSize" class="result-box">{{ item.boxSize }}</span>
            <svg
              v-if="isItemSelected(item.makat)"
              class="result-check"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <svg
              v-else
              class="result-add"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Selected Items Section -->
    <div class="selected-section">
      <div class="selected-header">
        <span class="selected-title">פריטים נבחרים</span>
        <span class="selected-count">{{ items.length }} פריטים</span>
        <button v-if="items.length > 0" class="clear-all-btn" @click="clearAll">נקה הכל</button>
      </div>

      <div v-if="items.length === 0" class="selected-empty">לא נבחרו פריטים</div>

      <div v-else class="selected-list">
        <div v-for="(item, index) in items" :key="item.makat" class="selected-item">
          <div class="item-reorder">
            <button class="reorder-btn" :disabled="index === 0" @click="moveUp(index)">
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <button
              class="reorder-btn"
              :disabled="index === items.length - 1"
              @click="moveDown(index)"
            >
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
          <span class="item-makat">{{ item.makat }}</span>
          <span class="item-name">{{ item.name }}</span>
          <span v-if="item.boxSize" class="item-box">{{ item.boxSize }}</span>
          <button class="remove-btn" @click="removeItem(item.makat)">
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.items-picker {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

/* Search Section */
.search-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.search-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.search-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.search-hint {
  font-size: 11px;
  color: var(--text-muted);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  height: 40px;
  padding: 0 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.search-input-wrapper:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-icon {
  flex-shrink: 0;
  color: var(--text-muted);
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.search-input::placeholder {
  color: var(--text-muted);
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-btn:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

/* Search Results */
.search-results {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
}

.search-loading,
.search-error,
.search-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.search-error {
  color: var(--color-error);
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.results-list {
  display: flex;
  flex-direction: column;
}

.result-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--border-color-light, var(--border-color));
  font-family: var(--font-family);
  text-align: right;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover:not(:disabled) {
  background: var(--bg-secondary);
}

.result-item.selected {
  background: rgba(99, 102, 241, 0.05);
  cursor: default;
}

.result-makat {
  flex-shrink: 0;
  font-size: 12px;
  font-family: var(--font-family-mono, monospace);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
}

.result-name {
  flex: 1;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-box {
  font-size: 11px;
  color: var(--text-muted);
}

.result-check {
  flex-shrink: 0;
  color: var(--color-success, #16a34a);
}

.result-add {
  flex-shrink: 0;
  color: var(--text-muted);
}

/* Selected Section */
.selected-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.selected-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.selected-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.selected-count {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  background: var(--bg-tertiary);
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
}

.clear-all-btn {
  margin-right: auto;
  padding: 4px 10px;
  font-size: 12px;
  font-family: var(--font-family);
  color: var(--color-error);
  background: transparent;
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-all-btn:hover {
  background: rgba(239, 68, 68, 0.1);
}

.selected-empty {
  padding: var(--spacing-lg);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  background: var(--bg-secondary);
  border: 1px dashed var(--border-color);
  border-radius: var(--border-radius);
}

.selected-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.selected-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color-light, var(--border-color));
}

.selected-item:last-child {
  border-bottom: none;
}

.item-reorder {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.reorder-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 14px;
  padding: 0;
  background: var(--bg-secondary);
  border: none;
  border-radius: 2px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.reorder-btn:hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.reorder-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.item-makat {
  flex-shrink: 0;
  font-size: 12px;
  font-family: var(--font-family-mono, monospace);
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
}

.item-name {
  flex: 1;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-box {
  font-size: 11px;
  color: var(--text-muted);
}

.remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.remove-btn:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.1);
}
</style>
